import { NookKind, useAskStream, useAskThread, useNooks } from "@nook/api-client"
import { AskAnswer, foldTurn, type AskAnswerView } from "@nook/client-ui"
import { ASK_QUESTION_MAX } from "@nook/schema"
import { Button, Dialog, DialogContent, DialogTitle, SidebarTrigger, Textarea } from "@nook/ui"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { ArrowUp, Square } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"

import { Hint, Keys, useKeys } from "@/components/keys.js"
import { NookPanel } from "@/components/nook-panel.js"
import { UpgradePrompt } from "@/components/upgrade-prompt.js"

export const Route = createFileRoute("/_authed/ask/$threadId")({
  validateSearch: (search: Record<string, unknown>): { q?: string } =>
    typeof search.q === "string" ? { q: search.q } : {},
  component: AskRoute,
})

const STATUS_NOTE = {
  streaming: "Still answering…",
  cancelled: "Stopped.",
  error: "This answer didn't finish.",
  complete: null,
}

/**
 * `/ask/$threadId` — one Ask thread (block 17). `new` plus `?q=` starts one;
 * when `turn` arrives the URL is replaced with the real ID, and the router
 * keeps this component mounted, so the stream carries on.
 *
 * The stored thread and the live turn render through the same `AskAnswer`: the
 * live turn stands in for its stored row until that row has settled. Threads
 * switch from the sidebar without a remount, so the live turn belongs to the
 * `threadId` it was asked on (`origin`) and renders nowhere else.
 */
function AskRoute() {
  const { threadId } = Route.useParams()
  const { q } = Route.useSearch()
  const navigate = useNavigate()
  const stream = useAskStream()
  const live = useMemo(() => foldTurn(stream.events), [stream.events])
  const [asked, setAsked] = useState("")
  const [draft, setDraft] = useState("")
  const startedFor = useRef<string | null>(null)
  const [expired, setExpired] = useState(false)
  const [origin, setOrigin] = useState<string | null>(null)
  const [viewing, setViewing] = useState<{ id: string; ids: string[] } | null>(null)
  // Where the last click or key landed, so an opened nook grows out of its citation.
  const anchor = useRef("50% 50%")
  const [from, setFrom] = useState(anchor.current)
  const scroller = useRef<HTMLDivElement>(null)
  const pinned = useRef(true)

  // The second clause is the render between `turn`'s replace landing and `origin` following it.
  const ours = origin === threadId || (origin === "new" && threadId === live.threadId)
  const id = threadId === "new" ? (ours ? live.threadId : null) : threadId
  const thread = useAskThread(id)
  const streaming = ours && stream.status === "streaming"

  const ask = (question: string, existing: string | null) => {
    setOrigin(threadId)
    setAsked(question)
    void stream.start({ question, ...(existing ? { threadId: existing } : {}) })
  }

  // Guarded by a ref, not by the effect's deps alone: StrictMode runs effects
  // twice, and a second start would be a second billed turn.
  useEffect(() => {
    if (threadId !== "new" || !q || startedFor.current === q) return
    startedFor.current = q
    ask(q, null)
  }, [threadId, q])

  // Only on the transition to a new thread ID — not on every render that still
  // carries a previous turn's ID while `/ask/new` is starting another.
  useEffect(() => {
    if (threadId === "new" && origin === "new" && live.threadId) {
      startedFor.current = null
      void navigate({ to: "/ask/$threadId", params: { threadId: live.threadId }, replace: true })
    }
  }, [live.threadId])

  // Left `/ask/new`: onto the thread its turn created, or elsewhere before `turn` arrived.
  useEffect(() => {
    if (origin === "new" && threadId !== "new") setOrigin(threadId === live.threadId ? threadId : null)
  }, [threadId])

  // Refused before the stream opened, so nothing was spent (block 20).
  useEffect(() => {
    if (live.error?.code === "plan_expired") setExpired(true)
  }, [live.error])

  const stored = thread.data?.messages ?? []
  const liveIndex = stored.findIndex((message) => message.id === live.messageId)
  const liveRow = stored[liveIndex]
  const settled = liveRow?.role === "assistant" && liveRow.status !== "streaming"
  const showLive = ours && stream.status !== "idle" && !settled
  const messages = showLive ? stored.filter((_, index) => index !== liveIndex && index !== liveIndex - 1) : stored
  const empty = !id && !showLive

  // Over the thread, paging through the nooks of the answer it was opened from.
  const openNook = (shown: AskAnswerView["shownNooks"]) => (id: string) => {
    setFrom(anchor.current)
    setViewing({ id, ids: shown.flatMap((card) => (card.nook ? [card.nookId] : [])) })
  }

  // The dialog is centred, so a viewport point is an offset from the viewport's middle.
  const aim = (x: number, y: number) => (anchor.current = `calc(50% + ${x}px - 50vw) calc(50% + ${y}px - 50vh)`)

  // Follows the answer as it grows, until the user scrolls away from the bottom.
  useEffect(() => {
    const element = scroller.current
    if (!showLive || !element?.firstElementChild) return
    pinned.current = true
    const observer = new ResizeObserver(() => pinned.current && element.scrollTo({ top: element.scrollHeight }))
    observer.observe(element.firstElementChild)
    return () => observer.disconnect()
  }, [showLive])

  const submit = () => {
    const question = draft.trim()
    if (!question || streaming) return
    setDraft("")
    ask(question, id)
  }

  useKeys({ Escape: () => streaming && !viewing && stream.cancel() })

  const field = (
    <Composer
      value={draft}
      onChange={setDraft}
      onSubmit={submit}
      onStop={stream.cancel}
      streaming={streaming}
      placeholder={id ? "Ask a follow-up…" : "Ask about your nooks…"}
    />
  )
  const composer = <div className="flex justify-center px-6 pb-6">{field}</div>

  return (
    <div className="flex h-full flex-col">
      {empty ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-6 px-6 pb-[12vh]">
          <SidebarTrigger className="absolute top-3 left-3 md:hidden" />
          <h1 className="text-[1.75rem] font-semibold tracking-tight">Ask Nook</h1>
          {field}
          <Suggestions onAsk={(question) => ask(question, null)} />
        </div>
      ) : (
        <>
          <header className="flex h-14 shrink-0 items-center gap-2 border-b px-6">
            <SidebarTrigger className="-ml-2 md:hidden" />
            <h1 className="truncate text-sm font-medium">
              {thread.data?.title ?? (ours ? live.title : null) ?? ((ours && asked) || "Ask Nook")}
            </h1>
          </header>

          <div
            ref={scroller}
            // Text fades out where it meets the composer rather than being cut.
            className="min-h-0 flex-1 overflow-y-auto [mask-image:linear-gradient(to_bottom,black_calc(100%-2rem),transparent)]"
            onScroll={({ currentTarget: el }) => (pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48)}
            onPointerDownCapture={(event) => aim(event.clientX, event.clientY)}
            onKeyDownCapture={(event) => {
              const rect = (event.target as HTMLElement).getBoundingClientRect()
              aim(rect.x + rect.width / 2, rect.y + rect.height / 2)
            }}
          >
            <div className="mx-auto flex max-w-3xl flex-col gap-8 px-6 py-8">
              {thread.isError && !showLive ? (
                <p className="text-muted-foreground text-sm">This thread isn&apos;t available.</p>
              ) : null}

              {messages.map((message) =>
                message.role === "user" ? (
                  <Question key={message.id} text={message.content} />
                ) : (
                  <div key={message.id} className="space-y-2">
                    <AskAnswer messageId={message.id} answer={message} streaming={false} onOpenNook={openNook(message.shownNooks)} />
                    {STATUS_NOTE[message.status] ? (
                      <p className="text-muted-foreground text-xs">{STATUS_NOTE[message.status]}</p>
                    ) : null}
                  </div>
                ),
              )}

              {showLive ? (
                <>
                  <Question text={asked} />
                  <div className="space-y-2">
                    <AskAnswer
                      messageId={live.messageId}
                      answer={live.answer}
                      streaming={streaming}
                      onOpenNook={openNook(live.answer.shownNooks)}
                    />
                    {live.error ? <p className="text-destructive text-xs">{live.error.message}</p> : null}
                    {stream.status === "cancelled" ? <p className="text-muted-foreground text-xs">Stopped.</p> : null}
                  </div>
                </>
              ) : null}
            </div>
          </div>

          {thread.isError && !showLive ? null : composer}
        </>
      )}

      <Dialog open={viewing !== null} onOpenChange={(open) => !open && setViewing(null)}>
        <DialogContent
          showCloseButton={false}
          aria-describedby={undefined}
          className="h-[min(88dvh,52rem)] overflow-hidden p-0 sm:max-w-4xl"
          style={{ transformOrigin: from }}
        >
          <DialogTitle className="sr-only">Nook</DialogTitle>
          {viewing ? (
            <NookPanel
              id={viewing.id}
              ids={viewing.ids}
              onOpen={(id) => setViewing({ ...viewing, id })}
              onClose={() => setViewing(null)}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <UpgradePrompt open={expired} onOpenChange={setExpired} />
    </div>
  )
}

/** The Ask box: ↵ sends, ⇧↵ breaks the line, and the send button turns into Stop while answering. */
function Composer({
  value,
  onChange,
  onSubmit,
  onStop,
  streaming,
  placeholder,
}: {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  onStop: () => void
  streaming: boolean
  placeholder: string
}) {
  return (
    <form
      className="bg-card flex w-full max-w-[720px] flex-col rounded-[20px] border shadow-[0_1px_2px_rgb(0_0_0/0.04),0_12px_32px_rgb(0_0_0/0.06)]"
      onSubmit={(event) => {
        event.preventDefault()
        onSubmit()
      }}
    >
      <Textarea
        aria-label="Ask"
        autoFocus
        maxLength={ASK_QUESTION_MAX}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault()
            onSubmit()
          }
        }}
        disabled={streaming}
        placeholder={placeholder}
        className="min-h-14 resize-none border-0 bg-transparent! px-5 pt-5 pb-2 text-base shadow-none focus-visible:ring-0 md:text-base"
      />
      <div className="text-muted-foreground flex items-center justify-end gap-2 px-3 pt-2.5 pb-3 text-xs">
        <Hint keys="Enter">send</Hint>
        <Hint keys="Shift+Enter">new line</Hint>
        {streaming ? (
          <Button type="button" variant="outline" size="sm" className="ml-2 rounded-full" onClick={onStop}>
            <Square />
            Stop
            <Keys keys="Escape" />
          </Button>
        ) : (
          <Button type="submit" size="icon" aria-label="Send" className="ml-2 size-8.5 rounded-full" disabled={!value.trim()}>
            <ArrowUp />
          </Button>
        )}
      </div>
    </form>
  )
}

/** Starting points made from the library itself, so each one has an answer. */
function Suggestions({ onAsk }: { onAsk: (question: string) => void }) {
  const latest = useNooks({ kind: NookKind.Capture }).data?.pages[0]?.items ?? []
  const video = latest.find((nook) => nook.platform === "youtube")
  const article = latest.find((nook) => nook.sourceTitle && !nook.platform && !nook.type)
  const questions = [
    "What did I nook this week?",
    article && `What does “${article.sourceTitle}” say?`,
    video && "Summarize the last video I saved",
  ].filter((question): question is string => Boolean(question))

  return (
    <div className="flex max-w-[720px] flex-wrap justify-center gap-2">
      {questions.map((question) => (
        <Button key={question} variant="outline" size="sm" className="h-8 max-w-full rounded-full font-normal" onClick={() => onAsk(question)}>
          <span className="truncate">{question}</span>
        </Button>
      ))}
    </div>
  )
}

function Question({ text }: { text: string }) {
  return <p className="bg-muted self-end rounded-[14px] px-4 py-2.5 text-sm">{text}</p>
}
